import React, { useCallback, useEffect, useRef, useState } from "react";

import {
	Button,
	Checkbox,
	Chip,
	IconButton,
	LinearProgress,
	makeStyles,
	MenuItem,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import {
	CheckCircleOutline,
	DeleteOutline,
	Edit,
	FileCopyOutlined,
	HighlightOff,
	PlayArrow,
	ReportProblemOutlined,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ScenarioModal from "./ScenarioModal";

const POLL_MS = 3000;

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(2),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	hint: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
	section: { margin: theme.spacing(2, 0, 1), fontWeight: 600 },
	row: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(0.5, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	grow: { flex: 1, minWidth: 0 },
	check: { display: "flex", gap: 6, alignItems: "flex-start", fontSize: 14, margin: "2px 0" },
	transcript: {
		background: theme.palette.action.hover,
		borderRadius: 6,
		padding: theme.spacing(1),
		margin: theme.spacing(1, 0, 1, 4),
		fontSize: 13,
		whiteSpace: "pre-wrap",
	},
	ok: { color: "#2e7d32" },
	bad: { color: "#c62828" },
	warn: { color: "#ed6c02" },
}));

const STATUS = {
	pass: { color: "ok", Icon: CheckCircleOutline },
	fail: { color: "bad", Icon: HighlightOff },
	error: { color: "warn", Icon: ReportProblemOutlined },
};

const Lab = () => {
	const classes = useStyles();
	const [scenarios, setScenarios] = useState([]);
	const [selected, setSelected] = useState({});
	const [runs, setRuns] = useState([]);
	const [runId, setRunId] = useState(null);
	const [run, setRun] = useState(null);
	const [open, setOpen] = useState({});
	const [modal, setModal] = useState({ open: false, scenario: null });
	const [confirmDelete, setConfirmDelete] = useState(null);
	const [starting, setStarting] = useState(false);
	const timer = useRef(null);

	const loadScenarios = useCallback(async () => {
		try {
			const { data } = await api.get("/lab/scenarios");
			setScenarios(data);
			setSelected(prev => {
				const next = {};
				data.forEach(s => {
					next[s.key] = prev[s.key] ?? true;
				});
				return next;
			});
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadRuns = useCallback(async () => {
		try {
			const { data } = await api.get("/lab/runs");
			setRuns(data);
			return data;
		} catch (err) {
			toastError(err);
			return [];
		}
	}, []);

	const loadRun = useCallback(async id => {
		try {
			const { data } = await api.get(`/lab/runs/${id}`);
			setRun(data);
			return data;
		} catch (err) {
			toastError(err);
			return null;
		}
	}, []);

	useEffect(() => {
		void loadScenarios();
		void loadRuns().then(list => {
			if (list.length) setRunId(list[0].id);
		});
	}, [loadScenarios, loadRuns]);

	useEffect(() => {
		clearTimeout(timer.current);
		if (!runId) return undefined;
		let cancelled = false;
		const tick = async () => {
			const data = await loadRun(runId);
			if (cancelled || !data) return;
			if (data.status === "running") {
				timer.current = setTimeout(tick, POLL_MS);
			} else {
				void loadRuns();
			}
		};
		void tick();
		return () => {
			cancelled = true;
			clearTimeout(timer.current);
		};
	}, [runId, loadRun, loadRuns]);

	const start = async () => {
		const keys = scenarios.filter(s => selected[s.key]).map(s => s.key);
		if (!keys.length) return toast.error(i18n.t("lab.pickOne"));
		setStarting(true);
		try {
			const { data } = await api.post("/lab/runs", { scenarioKeys: keys });
			setRunId(data.id);
			await loadRuns();
		} catch (err) {
			toastError(err);
		} finally {
			setStarting(false);
		}
		return null;
	};

	const remove = async () => {
		try {
			await api.delete(`/lab/scenarios/${confirmDelete.key.split(":")[1]}`);
			toast.success(i18n.t("lab.scenarioDeleted"));
			await loadScenarios();
		} catch (err) {
			toastError(err);
		}
		setConfirmDelete(null);
	};

	const running = run?.status === "running";

	const summary = s => {
		const e = s.definition.expect;
		const parts = [i18n.t("lab.turnsCount", { count: s.definition.turns.length })];
		if (e.handoff !== "any") parts.push(i18n.t(`lab.handoff.${e.handoff}`));
		if (s.definition.businessHours !== "open") parts.push(i18n.t(`lab.hours.${s.definition.businessHours}`));
		return parts.join(" · ");
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={i18n.t("lab.deleteTitle")}
				open={!!confirmDelete}
				onClose={() => setConfirmDelete(null)}
				onConfirm={remove}
			>
				{i18n.t("lab.deleteMessage")}
			</ConfirmationModal>
			<ScenarioModal
				open={modal.open}
				scenario={modal.scenario}
				onClose={() => setModal({ open: false, scenario: null })}
				onSaved={() => {
					setModal({ open: false, scenario: null });
					void loadScenarios();
				}}
			/>
			<MainHeader>
				<Title>{i18n.t("lab.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button variant="outlined" color="primary" onClick={() => setModal({ open: true, scenario: null })}>
						{i18n.t("lab.buttons.new")}
					</Button>
					<Button
						variant="contained"
						color="primary"
						startIcon={<PlayArrow />}
						disabled={starting || running}
						onClick={start}
					>
						{i18n.t("lab.buttons.run")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<Typography variant="body2" className={classes.hint}>
					{i18n.t("lab.hint")}
				</Typography>

				<Typography className={classes.section}>{i18n.t("lab.scenarios")}</Typography>
				{scenarios.map(s => (
					<div key={s.key} className={classes.row}>
						<Checkbox
							color="primary"
							checked={!!selected[s.key]}
							onChange={e => setSelected(prev => ({ ...prev, [s.key]: e.target.checked }))}
						/>
						<div className={classes.grow}>
							<Typography variant="body1">
								{s.name}{" "}
								{s.builtin && <Chip size="small" label={i18n.t("lab.builtin")} />}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								{summary(s)}
							</Typography>
						</div>
						<Tooltip title={i18n.t("lab.buttons.duplicate")}>
							<IconButton
								size="small"
								onClick={() =>
									setModal({
										open: true,
										scenario: { ...s, key: "new", builtin: true, name: `${s.name} (cópia)` },
									})
								}
							>
								<FileCopyOutlined fontSize="small" />
							</IconButton>
						</Tooltip>
						{!s.builtin && (
							<>
								<IconButton size="small" onClick={() => setModal({ open: true, scenario: s })}>
									<Edit fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={() => setConfirmDelete(s)}>
									<DeleteOutline fontSize="small" />
								</IconButton>
							</>
						)}
					</div>
				))}

				<Typography className={classes.section}>{i18n.t("lab.results")}</Typography>
				{runs.length > 0 && (
					<TextField
						select
						size="small"
						variant="outlined"
						value={runId || ""}
						onChange={e => setRunId(e.target.value)}
						style={{ minWidth: 300, marginBottom: 12 }}
					>
						{runs.map(r => (
							<MenuItem key={r.id} value={r.id}>
								#{r.id} · {new Date(r.createdAt).toLocaleString()} · {r.passed}/{r.total}
							</MenuItem>
						))}
					</TextField>
				)}
				{!run && <Typography color="textSecondary">{i18n.t("lab.noRuns")}</Typography>}
				{run && (
					<>
						{running && (
							<>
								<Typography variant="body2">
									{i18n.t("lab.running", { done: run.results.length, total: run.total })}
								</Typography>
								<LinearProgress variant="determinate" value={(run.results.length / run.total) * 100} />
							</>
						)}
						{run.status === "failed" && (
							<Typography className={classes.bad}>{run.error}</Typography>
						)}
						{run.status !== "running" && (
							<Typography variant="body2" style={{ marginBottom: 8 }}>
								{i18n.t("lab.summary", { passed: run.passed, total: run.total })}
							</Typography>
						)}
						{run.results.map(r => {
							const { color, Icon } = STATUS[r.status];
							return (
								<div key={r.key}>
									<div className={classes.row} style={{ cursor: "pointer" }} onClick={() => setOpen(prev => ({ ...prev, [r.key]: !prev[r.key] }))}>
										<Icon className={classes[color]} />
										<Typography className={classes.grow}>{r.name}</Typography>
										<Typography variant="caption" color="textSecondary">
											{i18n.t(`lab.status.${r.status}`)} · {(r.durationMs / 1000).toFixed(1)}s
										</Typography>
									</div>
									{open[r.key] && (
										<div style={{ padding: "8px 0 8px 32px" }}>
											{r.error && <Typography className={classes.warn}>{r.error}</Typography>}
											{r.checks.map(c => (
												<div key={c.id} className={classes.check}>
													{c.ok ? <CheckCircleOutline fontSize="small" className={classes.ok} /> : <HighlightOff fontSize="small" className={classes.bad} />}
													<span>
														{c.label}
														{c.detail && !c.ok ? ` — ${c.detail}` : ""}
													</span>
												</div>
											))}
											<div className={classes.transcript}>
												{r.transcript.turns
													.filter(t => !t.skipped)
													.map(t =>
														[`${i18n.t("lab.customer")}: ${t.customer}`]
															.concat(t.replies.map(x => `${i18n.t("lab.agent")}: ${x}`))
															.join("\n")
													)
													.join("\n\n")}
												{r.transcript.handoff &&
													`\n\n→ ${i18n.t("lab.handedOff")} (${r.transcript.handoff.reason || "—"})`}
											</div>
										</div>
									)}
								</div>
							);
						})}
					</>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Lab;
